/**
 * ImportExistingDocModal — prompt the writer when a non-empty Word document is
 * opened that has no associated Asthra run (no runId in its metadata). Offers to
 * "build on this document": pick a submission type + source library and read the
 * document's existing headings into a custom spine, rather than starting fresh.
 *
 * SRP: pure presentation. No state, no Word.js calls. The shell wires the
 * side-effects (start the custom-import orchestration, or dismiss).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 10, 30, 0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  dialog: {
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderTop: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    padding: "18px 18px 16px",
    maxWidth: "420px",
    width: "100%",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.25)",
  },
  title: {
    fontFamily: fonts.display,
    fontSize: "15px",
    fontWeight: 700,
    color: inkColors.ink,
    marginBottom: "6px",
  },
  body: {
    fontSize: "12px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  caveat: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    lineHeight: 1.4,
    marginTop: "10px",
    paddingLeft: "8px",
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
  },
  btn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    padding: "9px 14px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    border: "none",
  },
  btnPrimary: {
    background: brandColors.primary,
    color: "#fff",
  },
  btnSecondary: {
    background: "#fff",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
});

export interface ImportExistingDocModalProps {
  /** Writer chose "Build on this document" — shell starts the custom-import
   *  orchestration (doctype → library → register custom run). */
  onConfirmImport: () => void;
  /** Writer chose "Start a fresh draft instead" — shell launches the standard
   *  new-document flow (doctype → library → sections). */
  onStartFresh: () => void;
  /** Writer dismissed the prompt — shell records the dismissal so it doesn't
   *  re-offer for this document in this session. */
  onDismiss: () => void;
}

export const ImportExistingDocModal: React.FC<ImportExistingDocModalProps> = ({
  onConfirmImport,
  onStartFresh,
  onDismiss,
}) => {
  const styles = useStyles();
  return (
    <dialog open className={styles.backdrop} aria-modal="true" aria-labelledby="import-existing-doc-title">
      <div className={styles.dialog}>
        <div id="import-existing-doc-title" className={styles.title}>
          Build on this document?
        </div>
        <div className={styles.body}>
          This document already has content and isn&rsquo;t linked to a draft yet. You can build on it: pick a
          submission type and connect a source library, and its existing headings become the spine — so you can
          regenerate sections and run regulatory audits against the work you already have.
        </div>
        <div className={styles.caveat}>
          Your document is left exactly as it is — nothing is overwritten. Sections with content show a filled marker;
          empty headings show an open one. Prefer a clean slate? Start a fresh draft instead — your current content
          stays untouched until you choose to replace it.
        </div>
        <div className={styles.actions}>
          <button type="button" className={mergeClasses(styles.btn, styles.btnSecondary)} onClick={onStartFresh}>
            Start a fresh draft
          </button>
          <button type="button" className={mergeClasses(styles.btn, styles.btnPrimary)} onClick={onConfirmImport}>
            Build on this document
          </button>
        </div>
        <div className={styles.actions}>
          <button type="button" className={mergeClasses(styles.btn, styles.btnSecondary)} onClick={onDismiss}>
            Not now
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ImportExistingDocModal;
